// src/components/superuser/AddUserDialog.js
//
// Invite a new user to the superuser's client. Collects name, email, role, and
// property assignment (scoped to this client's properties), then calls the
// sendUserInvite cloud function — the same path AddUser uses — stamping the
// client's id so the new account lands scoped to this client.

import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth } from "../../firebase";
import { ASSIGNABLE_ROLES } from "./UserEditDialog";

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  userName: "",
  userType: "",
  propertyIds: [],
};

export default function AddUserDialog({
  open,
  onClose,
  clientId,
  clientProperties = [],
  onSent,
}) {
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(emptyForm);
      setError("");
    }
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const nameById = {};
  clientProperties.forEach((p) => (nameById[p.id] = p.displayName || p.id));

  const handleSend = async () => {
    const { firstName, lastName, email, userType, propertyIds } = form;
    if (!firstName || !lastName || !email || !userType || propertyIds.length === 0) {
      setError("Name, email, role, and at least one property are required.");
      return;
    }
    setSending(true);
    setError("");
    try {
      const properties = propertyIds.map((id) => ({
        id,
        displayName: nameById[id] || id,
        clientId,
      }));
      const fn = httpsCallable(getFunctions(), "sendUserInvite");
      const res = await fn({
        firstName,
        lastName,
        email,
        userName: form.userName || email.split("@")[0],
        userType,
        properties,
        clientId,
        invitedBy: auth.currentUser?.email || "superuser",
      });
      if (res?.data?.success === false) {
        throw new Error(res.data.error || "Failed to send invitation");
      }
      onSent?.({ severity: "success", message: `Invitation sent to ${email}` });
      onClose?.();
    } catch (err) {
      console.error("send invite failed", err);
      setError(err?.message || "Could not send invitation.");
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite User</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={form.firstName}
              onChange={set("firstName")}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={form.lastName}
              onChange={set("lastName")}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="email"
              label="Email"
              value={form.email}
              onChange={set("email")}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Role</InputLabel>
              <Select label="Role" value={form.userType} onChange={set("userType")}>
                {ASSIGNABLE_ROLES.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Username (optional)"
              value={form.userName}
              onChange={set("userName")}
              helperText="Defaults to the part before @"
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={clientProperties}
              value={clientProperties.filter((p) =>
                form.propertyIds.includes(p.id)
              )}
              onChange={(e, val) =>
                setForm((f) => ({ ...f, propertyIds: val.map((v) => v.id) }))
              }
              getOptionLabel={(o) => o.displayName || o.id}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              filterSelectedOptions
              noOptionsText="No properties found for this client"
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Assigned Properties"
                  placeholder="Search properties…"
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSend} disabled={sending}>
          {sending ? "Sending…" : "Send Invitation"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
